import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { SettledContent, SETTLED_STATE } from '@/shared/ui/animations';
import { EmptyStateTitle } from '@/shared/ui';
import {
  PROJECT_CAMPAIGNS_PAGE_SIZE,
  projectCampaignsQueryOptions,
} from '@/entities/media-campaign';
import { ScopeBreadcrumb, useScope } from '@/features/performance-scope';
import { PacingTable } from '@/widgets/performance-pacing';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import { ROUTES } from '@/shared/config/routes';

/**
 * Performance → Pacing.
 *
 * Renders `pacingPct` as delivered by the API - this view computes no pacing math of
 * its own (see `widgets/performance-pacing/lib/pacing-status.ts`). States are kept
 * apart the same way Overview and Manage campaigns keep them: a failed load must never
 * render as a project with nothing to pace.
 */
export function Pacing() {
  const { t } = useTranslation('projects');
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const projectId = project.platformProjectId;
  const { scope, setScope } = useScope(projectId);

  const campaigns = useQuery(projectCampaignsQueryOptions(projectId));

  const rows = campaigns.data ?? [];
  // Narrows the same list rather than requesting a filtered one - the endpoint has no
  // per-campaign filter, and the full list is already in hand.
  const scoped = scope.campaignId
    ? rows.filter((campaign) => campaign.id === scope.campaignId)
    : rows;
  const campaignName = scoped[0]?.name;

  // Absence is only provable while the page came back short: this list is one page deep, so
  // a project deeper than that can hold the scoped campaign without it appearing here, and
  // "isn't in this project" is too strong a claim to make on a maybe. Overview and Reports
  // gate the same message the same way.
  const scopeIsAbsent =
    scope.campaignId !== null && scoped.length === 0 && rows.length < PROJECT_CAMPAIGNS_PAGE_SIZE;

  // One crossfade for the whole answer, so the table and the invitation to add
  // a campaign never dissolve into an identical copy of themselves.
  const viewState = campaigns.isError
    ? SETTLED_STATE.error
    : !campaigns.isLoading && (rows.length === 0 || scopeIsAbsent)
      ? SETTLED_STATE.empty
      : SETTLED_STATE.rows;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4" data-testid="performance-pacing-view">
      <ScopeBreadcrumb scope={scope} campaignName={campaignName} onWiden={setScope} />

      <SettledContent
        state={viewState}
        className="min-h-0 flex-1"
        contentClassName="flex min-h-0 flex-1 flex-col gap-4"
      >
        {campaigns.isError ? (
          <Empty role="alert" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performancePacing.couldNotLoadThisProjectsPacing')}
              </EmptyStateTitle>
              <EmptyDescription>
                {campaigns.error instanceof Error
                  ? campaigns.error.message
                  : t('projectDetail.performancePacing.theRequestDidNotComeBack')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={() => void campaigns.refetch()}>
                {t('projectDetail.performancePacing.tryAgain')}
              </Button>
            </EmptyContent>
          </Empty>
        ) : !campaigns.isLoading && rows.length === 0 ? (
          <Empty className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performancePacing.noCampaignsYet')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performancePacing.thisViewPacesTheCampaigns')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" asChild>
                <Link to={ROUTES.PROJECT_PERFORMANCE_CAMPAIGNS(projectId)}>
                  {t('projectDetail.performancePacing.manageCampaigns')}
                </Link>
              </Button>
            </EmptyContent>
          </Empty>
        ) : !campaigns.isLoading && scopeIsAbsent ? (
          // Distinct from "no campaigns yet" above: `rows` is non-empty, so this project has
          // campaigns - the scope just does not name one of them. Reusing that copy ("Add
          // one") would tell a marketer to create a campaign that may already exist, and
          // `PacingTable`'s own internal empty state ("No campaigns to pace") is no better -
          // it reads as the whole project having nothing, which is false here.
          <Empty role="status" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>
                {t('projectDetail.performancePacing.thisCampaignIsntInThisProject')}
              </EmptyStateTitle>
              <EmptyDescription>
                {t('projectDetail.performancePacing.theCampaignThisLinkPointsTo')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <PacingTable campaigns={scoped} isLoading={campaigns.isLoading} />
        )}
      </SettledContent>
    </div>
  );
}
